import { localMessagingStore, DRAFT_TTL } from './localMessagingStore';

const recordKey = (kind, id) => `${kind}:${id}`;
export function createDraftRepository({ store = localMessagingStore, now = Date.now,
  channelFactory = name => typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(name) } = {}) {
  let owner = null, generation = 0, channel, heartbeat, revision = 0, records = new Map(), opened = new Map(), peers = new Map(), ready = false;
  const listeners = new Set(), timers = new Map(), writes = new Map();
  const tab = `${Date.now()}-${Math.random()}`;
  const notify = () => listeners.forEach(listener => listener());
  const newer = (next, current) => !current || next.edited_at > current.edited_at || (next.edited_at === current.edited_at && `${next.writer}:${next.revision}` > `${current.writer}:${current.revision}`);
  const announce = () => channel?.postMessage({ type: 'open', owner, tab, ids: [...opened.keys()] });
  async function persist(key) {
    clearTimeout(timers.get(key)); timers.delete(key);
    const record = records.get(key), actor = owner, ticket = generation;
    if (!record || actor == null) return;
    const previous = writes.get(key) || Promise.resolve();
    const work = previous.catch(() => {}).then(async () => {
      if (ticket !== generation || record !== records.get(key)) return;
      // Keep deletion tombstones until TTL: an older tab must not resurrect sent/discarded drafts.
      if (store.putLatest) await store.putLatest(record);
      else await store.put(record);
      if (ticket !== generation) await store.remove(actor, record.store_kind, record.id);
    });
    writes.set(key, work);
    await work;
    if (writes.get(key) === work) writes.delete(key);
  }
  return {
    async session(actor) {
      if (actor === owner) return;
      const previous = owner, ticket = ++generation; owner = actor; records = new Map(); opened = new Map(); peers = new Map(); ready = false;
      timers.forEach(clearTimeout); timers.clear(); channel?.close(); clearInterval(heartbeat); notify();
      if (previous != null) await store.clearOwner(previous);
      if (actor == null || ticket !== generation) return;
      channel = channelFactory(`srm-drafts-${actor}`);
      if (channel) channel.onmessage = message => {
        const value = message.data; if (value?.owner !== owner || value.tab === tab) return;
        if (value.type === 'draft') {
          const key = recordKey(value.record.store_kind, value.record.id);
          if (newer(value.record, records.get(key))) { records.set(key, value.record); clearTimeout(timers.get(key)); timers.delete(key); notify(); }
        } else if (value.type === 'open') { peers.set(value.tab, { ids: value.ids, received: now() }); notify(); }
        else if (value.type === 'hello') announce();
      };
      channel?.postMessage({ type: 'hello', owner, tab }); announce();
      heartbeat = setInterval(() => { announce(); peers.forEach((peer, id) => { if (now() - peer.received > 75000) peers.delete(id); }); notify(); }, 30000);
      try {
        const saved = await store.list(actor);
        if (ticket !== generation) return;
        for (const record of saved.filter(item => ['chat-draft', 'query-draft'].includes(item.store_kind))) {
          if (now() - record.edited_at >= DRAFT_TTL) await store.remove(actor, record.store_kind, record.id);
          else { const key = recordKey(record.store_kind, record.id); if (newer(record, records.get(key))) records.set(key, record); }
        }
      } finally { if (ticket === generation) { ready = true; notify(); } }
    },
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); },
    get(actor, kind, id) { const record = actor === owner ? records.get(recordKey(kind, id)) : null; return record && !record.deleted && now() - record.edited_at < DRAFT_TTL ? record : null; },
    isReady: actor => actor === owner && ready,
    isOpen: id => opened.has(id) || [...peers.values()].some(peer => now() - peer.received < 75000 && peer.ids.includes(id)),
    open(id) { const ticket = generation; opened.set(id, (opened.get(id) || 0) + 1); announce(); notify(); return () => { if (ticket !== generation) return; if (opened.get(id) > 1) opened.set(id, opened.get(id) - 1); else opened.delete(id); announce(); notify(); }; },
    edit(actor, kind, id, value, delay = 400) {
      if (actor == null || actor !== owner || !id) return;
      const key = recordKey(kind, id);
      const deleted = value == null || (kind === 'chat-draft' && !value.text?.trim() && !value.reply && !value.attachments?.length && !value.voice);
      const record = { user_id: actor, store_kind: kind, id, value, edited_at: Math.max(now(), (records.get(key)?.edited_at || 0) + 1), revision: ++revision, writer: tab, deleted };
      records.set(key, record); notify(); channel?.postMessage({ type: 'draft', owner, tab, record });
      clearTimeout(timers.get(key));
      if (delay) timers.set(key, setTimeout(() => persist(key).catch(() => {}), delay));
      else persist(key).catch(() => {});
    },
    flushAll() { return Promise.all([...timers.keys()].map(key => persist(key))); },
    flush(actor, kind, id) { if (actor !== owner) return Promise.resolve(); return persist(recordKey(kind, id)); },
    dispose() { owner = null; generation++; timers.forEach(clearTimeout); clearInterval(heartbeat); channel?.close(); listeners.clear(); },
  };
}
export const localDrafts = createDraftRepository();
// Flush debounce buffers on navigation/sleep as well as React drawer cleanup.
const flushDrafts = () => localDrafts.flushAll().catch(() => {});
window.addEventListener("pagehide", flushDrafts);
window.addEventListener("beforeunload", flushDrafts);
document.addEventListener("visibilitychange", () => { if (document.hidden) flushDrafts(); });
export function draftPreview(value) {
  const file = value?.voice?.file || value?.attachments?.[0]?.file;
  const kind = value?.voice ? 'audio' : file?.type?.split('/')[0];
  const media = kind === 'image' ? ['📷', 'Photo'] : kind === 'video' ? ['🎥', 'Video'] : kind === 'audio' ? ['🎤', 'Voice message'] : file ? ['📄', 'Document'] : ['', ''];
  return { icon: media[0], text: value?.text?.trim() || value?.attachments?.[0]?.caption || media[1] || (value?.reply ? 'Reply' : '') };
}
